import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/motion/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CTASection from "@/components/CTASection";
import { ArrowUpRight } from "lucide-react";

export default function CaseStudies() {
  const { t } = useLang();
  const cs = t.caseStudies;

  return (
    <div data-testid="case-studies-page">
      <section className="relative pt-40 pb-16 overflow-hidden">
        <div
          className="absolute -top-20 right-1/4 w-[600px] h-[600px] pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(0,229,255,0.10), transparent 60%)" }}
        />
        <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={cs.heroEyebrow} title={cs.heroTitle} sub={cs.heroSub} />
        </div>
      </section>

      <section className="pb-12">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 space-y-6">
          {cs.items.map((item, i) => (
            <Reveal key={i} delay={0.05}>
              <article
                data-testid={`case-${i}`}
                className="group border border-white/10 bg-navy-surface/40 overflow-hidden hover:border-cyan-precision/40 transition-colors duration-300"
              >
                <div className="grid grid-cols-1 lg:grid-cols-12">
                  {/* Left: meta + narrative */}
                  <div className="lg:col-span-8 p-8 lg:p-12 lg:border-r border-white/10">
                    <div className="flex items-center gap-4">
                      <span className="font-mono-tech text-5xl font-bold text-[#F0F4F8]/10">
                        0{i + 1}
                      </span>
                      <div>
                        <span className="font-mono-tech text-[11px] uppercase tracking-widest text-cyan-precision">
                          {item.sector}
                        </span>
                        <span className="mx-2 text-[#94A3B8]/40">·</span>
                        <span className="font-mono-tech text-[11px] uppercase tracking-widest text-[#94A3B8]">
                          {item.tag}
                        </span>
                      </div>
                    </div>

                    <h3 className="mt-6 font-display text-2xl lg:text-3xl font-light tracking-tight text-[#F0F4F8] leading-tight">
                      {item.title}
                    </h3>

                    <div className="mt-8 space-y-6">
                      <div>
                        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#94A3B8] mb-2">
                          {cs.challengeLabel}
                        </div>
                        <p className="text-[#94A3B8] leading-relaxed">{item.challenge}</p>
                      </div>
                      <div>
                        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-cyan-precision mb-2">
                          {cs.solutionLabel}
                        </div>
                        <p className="text-[#F0F4F8]/85 leading-relaxed">{item.solution}</p>
                      </div>
                    </div>

                    <blockquote className="mt-8 border-l-2 border-cyan-precision/50 pl-5">
                      <p className="font-display text-lg font-light text-[#F0F4F8] italic leading-snug">
                        “{item.quote}”
                      </p>
                      <footer className="mt-3 font-mono-tech text-[11px] uppercase tracking-widest text-[#94A3B8]">
                        {item.author}
                      </footer>
                    </blockquote>
                  </div>

                  {/* Right: results */}
                  <div className="lg:col-span-4 p-8 lg:p-12 bg-navy/40 flex flex-col justify-center">
                    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#94A3B8] mb-6">
                      {cs.resultLabel}
                    </div>
                    <div className="space-y-8">
                      {item.results.map((r, j) => (
                        <div key={j}>
                          <div className="font-display text-5xl font-light text-cyan-precision tracking-tighter">
                            {r.metric}
                          </div>
                          <div className="mt-1 text-sm text-[#94A3B8]">{r.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}

          <p className="mt-6 font-mono-tech text-[11px] text-[#94A3B8]/60">{cs.note}</p>
        </div>
      </section>

      <CTASection title={cs.ctaTitle} body={cs.ctaBody} btn={cs.ctaBtn} />
    </div>
  );
}
